import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ChatRoundedIcon from '@mui/icons-material/ChatRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded'
import { Modal } from '@mui/material'
import MessageItem from './MessageItem'
import NewMessageForm from './NewMessageForm'
import {
  deleteConversation,
  getMessages,
  resetDeleteData,
} from '../../features/messenger/conversationSlice'
import { fetchPublicKey } from 'services/e2ee/keyInit'
import Loader from 'loaders/Loader'
import useToggleDropdown from 'hooks/useToggleDropdown'
import { setConfirmModalData, setViewConfirmModal } from 'features/modal/modalSlice'
import ConfirmModal from 'components/modals/ConfirmModal'
import { imageDefaults } from 'components/utils/imageDefaults'
import { useTranslation } from 'react-i18next'

function MessagePanel() {
  const { t } = useTranslation('messaging')
  const menuRef = useRef(null)
  const bottomRef = useRef(null)
  const dispatch = useDispatch()
  const { convId } = useParams()
  const navigate = useNavigate()
  const [isDropped, setDropdown] = useToggleDropdown(menuRef)
  const [otherPartyPublicKey, setOtherPartyPublicKey] = useState(null)
  const { user } = useSelector((state) => state.auth)
  const {
    messages,
    getMessagesStatus,
    hasMore,
    deleteConversationLoading,
    deleteConversationSuccess,
  } = useSelector((state) => state.conversations)
  const { viewConfirmModal, confirmModalData } = useSelector((state) => state.modal)

  const conversationDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(`${convId}`)) || {}
    } catch {
      return {}
    }
  }, [convId])

  useEffect(() => {
    let active = true
    const init = async () => {
      const otherId = conversationDetails.otherUserId ?? conversationDetails.id ?? null
      let publicKey = null
      if (otherId && user?.token) {
        try {
          publicKey = await fetchPublicKey(otherId, user.token)
        } catch {}
      }
      if (!active) return
      setOtherPartyPublicKey(publicKey)
      dispatch(getMessages({ conversationId: convId, otherPartyPublicKey: publicKey }))
    }
    init()
    return () => { active = false }
  }, [conversationDetails, convId, dispatch, user?.token])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages?.length])

  const handleDeleteConversation = () => {
    setDropdown.off(menuRef)
    dispatch(setConfirmModalData({
      text: t('conversation.leaveTitle'),
      extraInfo: t('conversation.leaveBody'),
      handler: () => dispatch(deleteConversation({ conversationId: convId })),
      confirmBtnBackgroundHover: '#b62309',
      loadingState: deleteConversationLoading,
      conditionToCloseModal: deleteConversationSuccess,
      thingsToResetAfterClose: () => dispatch(resetDeleteData()),
    }))
    dispatch(setViewConfirmModal(true))
  }

  const handleResetAfterClose = () => {
    dispatch(resetDeleteData())
    navigate('/messages')
  }

  return (
    <Panel>
      <MobileBack type='button' onClick={() => navigate('/messages')}>
        <ArrowBackRoundedIcon /> {t('conversation.back')}
      </MobileBack>

      <Header>
        <ConversationIdentity>
          <img
            src={conversationDetails.image || imageDefaults.avatar}
            alt={t('conversation.profileAlt')}
          />
          <div>
            <h1>{conversationDetails.name || t('participants.member')}</h1>
            <EncryptionStatus $encrypted={Boolean(otherPartyPublicKey)}>
              {otherPartyPublicKey ? <LockRoundedIcon /> : <LockOpenRoundedIcon />}
              {otherPartyPublicKey
                ? t('encryption.endToEndEncrypted')
                : t('encryption.notEncrypted')}
            </EncryptionStatus>
          </div>
        </ConversationIdentity>

        <SettingsButton
          ref={menuRef}
          type='button'
          aria-label={t('actions.title')}
          onClick={() => setDropdown.toggle(!isDropped)}
        >
          <MoreHorizRoundedIcon />
        </SettingsButton>

        {isDropped && (
          <SettingsMenu>
            <strong>{t('actions.title')}</strong>
            <button type='button' onClick={handleDeleteConversation}>
              <PersonRemoveRoundedIcon />
              {t('conversation.leave')}
            </button>
          </SettingsMenu>
        )}
      </Header>

      <MessageArea>
        {hasMore && (
          <LoadOlder type='button' disabled>
            {t('conversation.loadMore')}
          </LoadOlder>
        )}

        {getMessagesStatus === 'LOADING' ? (
          <CenterState><Loader /></CenterState>
        ) : getMessagesStatus === 'SUCCESS' && messages?.length ? (
          messages.map((message) => (
            <MessageItem
              key={message._id}
              image={message.attachments?.[0]?.url || null}
              isMine={message.sender.id === user._id}
              text={message.text}
              dateSent={message.createdAt}
              senderName={conversationDetails.name}
            />
          ))
        ) : (
          <CenterState>
            <ChatRoundedIcon />
            <strong>{t('conversation.noMessages')}</strong>
          </CenterState>
        )}
        <div ref={bottomRef} />
      </MessageArea>

      <NewMessageForm id={convId} recipientPublicKey={otherPartyPublicKey} />

      <Modal
        open={viewConfirmModal}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClose={() => dispatch(setViewConfirmModal(false))}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <ConfirmModal
          text={confirmModalData.text}
          extraInfo={confirmModalData.extraInfo}
          handler={confirmModalData.handler}
          confirmBtnBackgroundHover={confirmModalData.confirmBtnBackgroundHover}
          conditionToCloseModal={deleteConversationSuccess}
          doSomethingAfterClose
          resetAfterClose={handleResetAfterClose}
          loadingState={deleteConversationLoading}
        />
      </Modal>
    </Panel>
  )
}

export default MessagePanel

const Panel = styled.main`
  position: relative;
  width: 700px;
  min-width: 550px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 25px;
  background: white;
  box-shadow: 0 0 5px #ccc;

  @media (max-width: 1268px) {
    min-width: 400px;
    width: 550px;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    border-radius: 20px;
  }
`

const Header = styled.header`
  position: relative;
  flex: 0 0 auto;
  min-height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid #e8ecee;
`

const ConversationIdentity = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 11px;

  > img {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
  }

  h1 {
    margin: 0;
    color: #334044;
    font-size: 15px;
  }
`

const EncryptionStatus = styled.p`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 0 0;
  color: ${({ $encrypted }) => ($encrypted ? '#788387' : '#9a6a43')};
  font-size: 10px;

  svg {
    font-size: 11px;
  }
`

const SettingsButton = styled.button`
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #edf1f2;
  color: #526064;
  cursor: pointer;

  &:hover {
    background: #e2e8ea;
  }
`

const SettingsMenu = styled.div`
  position: absolute;
  z-index: 5;
  top: 60px;
  right: 16px;
  width: 210px;
  padding: 12px;
  border: 1px solid #e1e6e8;
  border-radius: 12px;
  background: white;
  box-shadow: 0 16px 40px rgba(20, 35, 40, 0.16);

  strong {
    display: block;
    margin-bottom: 8px;
    color: #354145;
    font-size: 12px;
  }

  button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border: 0;
    border-radius: 8px;
    background: #edf1f2;
    color: #354145;
    cursor: pointer;
    text-align: left;

    svg {
      font-size: 17px;
    }
  }
`

const MessageArea = styled.section`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  background: #f6f8f9;
  scrollbar-width: thin;
`

const CenterState = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #788387;
  text-align: center;

  > svg {
    font-size: 40px;
    color: #a0abad;
  }

  strong {
    color: #435055;
  }
`

const LoadOlder = styled.button`
  display: block;
  margin: 0 auto 16px;
  padding: 8px 14px;
  border: 0;
  border-radius: 18px;
  background: #e5eaec;
  color: #526064;
  font-size: 11px;
  font-weight: 700;
`

const MobileBack = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 10px 14px;
    border: 0;
    background: white;
    color: var(--themeColor);
    font-weight: 700;
  }
`
